import { useLocation } from 'react-router-dom';
import { updateQueryParams } from 'core/utils/updateQueryParams';

type Args = {
  /** 総ページ数。 */
  totalPages: number;
  /** 現在のページ。 */
  currentPageState: number;
  /** 現在のページを設定するイベント。 */
  setCurrentPageState: (page: number) => void;
};

type ReturnValue = {
  /** ページ番号の配列。省略記号を含む。 */
  pageNumbers: (number | '...')[];
  /** 次のページに移動するイベント。 */
  handleClickNextPage: () => void;
  /** 前のページに移動するイベント。 */
  handleClickPreviousPage: () => void;
  /** 指定したページ番号に移動するイベント。 */
  handleClickPageNumber: (page: number) => void;
};

export const usePageNation = ({
  totalPages,
  currentPageState,
  setCurrentPageState,
}: Args): ReturnValue => {
  const location = useLocation();

  const generatePageNumbers = (): (number | '...')[] => {
    const maxVisiblePages = 10;
    const pagesBeforeCurrent = 4;
    const pagesAfterCurrent = 4;

    if (totalPages <= maxVisiblePages) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const result: (number | '...')[] = [];
    const startPage = Math.max(1, currentPageState - pagesBeforeCurrent);
    const endPage = Math.min(totalPages, currentPageState + pagesAfterCurrent);

    if (startPage > 1) {
      result.push(1);
      if (startPage > 2) {
        result.push('...');
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      result.push(i);
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        result.push('...');
      }
      result.push(totalPages);
    }

    return result;
  };

  const handleClickNextPage = () => {
    setCurrentPageState(currentPageState + 1);
  };

  const handleClickPreviousPage = () => {
    setCurrentPageState(currentPageState - 1);
  };

  const handleClickPageNumber = (newPage: number) => {
    setCurrentPageState(newPage);

    const params = new URLSearchParams(location.search);
    params.set('page', String(newPage));
    updateQueryParams({ location, params });
  };

  return {
    pageNumbers: generatePageNumbers(),
    handleClickNextPage,
    handleClickPreviousPage,
    handleClickPageNumber,
  };
};
